import React, { useState, useEffect, useContext } from 'react';
import { Modal, Box, SpaceBetween, Button, Alert, Link, Spinner } from '@amzn/awsui-components-react';
import { GlobalAppContext } from 'src/components/App';
import {
    PromotionComponentType,
    PromotionStatus,
    PromotionStageInfo,
    PromoteComponentResponse,
    ValidateDependenciesResponse,
    getPromotionComponentHref,
} from 'src/models/promotion/PromotionTypes';

interface PromotionConfirmationModalProps {
    visible: boolean;
    componentName: string;
    componentType: PromotionComponentType;
    componentId: string;
    componentVersion?: number;
    stageInfo: PromotionStageInfo;
    onDismiss: () => void;
    /** Called with the accepted Promote API response; BLOCKED/FAILED are handled in the modal. */
    onPromotionStarted: (response: PromoteComponentResponse) => void;
    selectedRows?: string[];
}

export function PromotionConfirmationModal(props: PromotionConfirmationModalProps) {
    const {
        visible, componentName, componentType, componentId,
        componentVersion, stageInfo, onDismiss, onPromotionStarted, selectedRows,
    } = props;
    const { services } = useContext(GlobalAppContext);

    const [validating, setValidating] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidateDependenciesResponse | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [promoting, setPromoting] = useState(false);
    const [promotionError, setPromotionError] = useState<string | null>(null);
    const [promotionBlocked, setPromotionBlocked] = useState(false);

    useEffect(() => {
        if (!visible) {
            setValidating(false);
            setValidationResult(null);
            setValidationError(null);
            setPromoting(false);
            setPromotionError(null);
            setPromotionBlocked(false);
            return;
        }
        const validate = async () => {
            setValidating(true);
            try {
                const result = await services.promotionService.validateDependencies({
                    componentType,
                    componentId,
                    componentVersion,
                });
                setValidationResult(result);
            } catch (error: any) {
                setValidationError(error.message || 'Failed to validate promotion dependencies.');
            } finally {
                setValidating(false);
            }
        };
        validate();
    }, [visible]);

    const canPromote = validationResult?.canPromote ?? false;
    const missingDependencies = validationResult?.missingDependencies ?? [];

    // BB / COA / CDT skip the approval workflow — they land Active in the target stage.
    const componentTypesLandingActive: PromotionComponentType[] = [
        PromotionComponentType.BEGINNING_BALANCE,
        PromotionComponentType.CUSTOM_COA,
        PromotionComponentType.CUSTOM_DATA_TABLE,
    ];
    const landsActive = componentTypesLandingActive.includes(componentType);

    const handleConfirm = async () => {
        setPromoting(true);
        setPromotionError(null);
        try {
            const response = await services.promotionService.promote({
                componentType,
                componentId,
                componentVersion,
                selectedRows,
            });
            if (response.status === PromotionStatus.BLOCKED) {
                setPromotionError(response.message || 'A promotion is already in progress for this component.');
                setPromotionBlocked(true);
                setPromoting(false);
            } else if (response.status === PromotionStatus.FAILED) {
                setPromotionError(response.message || 'Promotion failed.');
                setPromoting(false);
            } else {
                onPromotionStarted(response);
            }
        } catch (error: any) {
            setPromotionError(error.message || 'An error occurred during promotion.');
            setPromoting(false);
        }
    };

    const handleDismiss = () => {
        onDismiss();
    };

    const renderContent = () => {
        if (validating) {
            return (
                <Box textAlign="center" padding="l">
                    <Spinner size="large" />
                    <Box margin={{ top: 's' }}>Validating dependencies...</Box>
                </Box>
            );
        }

        if (validationError) {
            return <Alert type="error">{validationError}</Alert>;
        }

        return (
            <SpaceBetween size="m">
                <Box>
                    {selectedRows && selectedRows.length > 0 ? (
                        <>
                            Promote <Box variant="span" fontWeight="bold">{selectedRows.length} rows</Box> from{' '}
                            <Box variant="span" fontWeight="bold">{componentName}</Box> to {stageInfo.to.toLowerCase()}?
                        </>
                    ) : (
                        <>
                            You can promote <Box variant="span" fontWeight="bold">{componentName}</Box> from
                            {' '}{stageInfo.from} environment into ATP's {stageInfo.to} environment.
                        </>
                    )}
                </Box>

                {canPromote && (
                    <Alert type="info">
                        {landsActive
                            ? <>The promoted component will land in <Box variant="span" fontWeight="bold">Active</Box> status in the {stageInfo.to} environment — no approval workflow is required.</>
                            : <>The promoted component will be in pending approval status in the {stageInfo.to} environment; it will still need to go through the standard approval workflow to become active.</>
                        }
                    </Alert>
                )}

                {!canPromote && missingDependencies.length > 0 && (
                    <Alert type="warning">
                        This component has the following unpromoted dependencies. You cannot promote a
                        component unless all its dependencies already exist in the {stageInfo.to} environment.
                        <Box margin={{ top: 's' }}>
                            {missingDependencies.map((dep, index) => {
                                const label = dep.componentName || `${dep.componentType} - ${dep.componentId}`;
                                const href = getPromotionComponentHref(dep.componentType, dep.componentId);
                                return (
                                    <Box key={index} margin={{ top: 'xxs' }}>
                                        {href
                                            ? <Link href={href} external={true}>{label}</Link>
                                            : <Box variant="span" color="text-status-inactive">{label}</Box>
                                        }
                                    </Box>
                                );
                            })}
                        </Box>
                    </Alert>
                )}

                {promotionError && (
                    <Alert type="error">
                        {promotionError}
                    </Alert>
                )}
            </SpaceBetween>
        );
    };

    return (
        <Modal
            visible={visible}
            onDismiss={handleDismiss}
            header={`Promote to ${stageInfo.to}`}
            footer={
                !validating && !validationError && (
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={handleDismiss}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleConfirm}
                                disabled={!canPromote || promoting || promotionBlocked}
                                loading={promoting}
                            >
                                Promote
                            </Button>
                        </SpaceBetween>
                    </Box>
                )
            }
        >
            {renderContent()}
        </Modal>
    );
}
